import React, { useMemo } from 'react';

import classnames from 'classnames';

import { Typography } from '@mui/material';
import Alert from '@mui/material/Alert';

import SpriteIcon from 'components/uiLibrary/SpriteIcon';

import CommonTrans from 'components/Globals/CommonTrans';

import { useShowPageAccessPortalForCasting } from 'components/Globals/PortalsProvider';

import { SUSPENSION_CATEGORY, TP } from 'constants/consts';

import useAppContext from 'utils/hooks/useAppContext';
import { useDialogs } from 'utils/hooks/useDialogs';
import usePageContext from 'utils/hooks/usePageContext';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES, ENTITY_VALIDATION_STATUS } from 'operabase-router/constants';

import NextLink from '../NextLink';

import classes from './EntityStatus.module.scss';

const EntityStatus = ({ entityInfo, entityType, className, validationStatus, isPaidEntity }) => {
  const { navigate, edit } = usePageContext();
  const { isLoggedIn } = useAppContext();
  const { t } = useTranslation('NS_APP_GLOBALS');
  const suspension = entityInfo?.suspension;
  const { setIsLoginDialog, setIsPermissionDeniedModalOpen } = useDialogs();
  const showPageAccessPortal = useShowPageAccessPortalForCasting();

  const { linkProps, newEntity } = useMemo(() => {
    if (suspension?.operationalAgency?.id) {
      return {
        linkProps: navigate.getLinkProps({ entity: suspension.operationalAgency, entityType }),
        newEntity: suspension?.operationalAgency?.name,
      };
    }

    if (suspension?.operationalOrganization?.id) {
      return {
        linkProps: navigate.getLinkProps({ entity: suspension.operationalOrganization, entityType }),
        newEntity: suspension?.operationalOrganization?.name,
      };
    }

    return { linkProps: {}, newEntity: '' };
  }, [entityType, navigate, suspension?.operationalAgency, suspension?.operationalOrganization]);

  const isEntityMerged = suspension?.category === SUSPENSION_CATEGORY.ACQUISITION;
  const isEntityRebranded = suspension?.category === SUSPENSION_CATEGORY.REBRANDING;
  const isNonOperational = suspension?.category === SUSPENSION_CATEGORY.REGULAR;

  const handlePaywallClick = () => {
    if (!isLoggedIn) {
      setIsLoginDialog({ isOpen: true });
    } else {
      showPageAccessPortal();
    }
  };

  const rootClassName = classnames(classes.alert, className);

  if (validationStatus === ENTITY_VALIDATION_STATUS.APPROVED_HIDDEN) {
    const key = entityType === ENTITY_TYPES.PROFILE ? 'FN_HIDDEN_PROFILE_BANNER_TITLE' : 'FN_HIDDEN_ORG_BANNER_TITLE';

    return (
      <Alert className={rootClassName} severity="info">
        {t(`${TP}.${key}`)}
      </Alert>
    );
  }

  if (!isPaidEntity && !edit && [ENTITY_TYPES.ORGANIZATION, ENTITY_TYPES.PROFILE].includes(entityType)) {
    const keyMap = {
      [ENTITY_TYPES.PROFILE]: ['CASTING_HEADER_1', 'm_ARTICLES_CLICK_HERE', 'CONVERSION_BANNER_TEXT_ARTIST_2'],
      [ENTITY_TYPES.ORGANIZATION]: [
        'FN_INDUSTRY_PROFESSIONALS',
        'm_ARTICLES_CLICK_HERE',
        'COVERSION_TO_ACEESS_PROFESSIONALS_TEXT',
      ],
    };
    const keys = keyMap[entityType];

    return (
      <div className={classes.conversionFunnelLinkContainer}>
        <div className={classes.conversionFunnelLink} onClick={handlePaywallClick}>
          <Typography variant="p" fontSize={11}>
            {t(`${TP}.${keys[0]}`)}
            <span className={classes.clickHereText}>{t(`${TP}.${keys[1]}`)}</span>
            {t(`${TP}.${keys[2]}`)}
            <SpriteIcon icon="chevron_right" className={classes.conversionFunnelLink__chevron} />
          </Typography>
        </div>
      </div>
    );
  }

  if (isEntityMerged) {
    return (
      <Alert className={rootClassName} icon={false}>
        <CommonTrans
          ns="NS_APP_GLOBALS"
          i18nKey={`${TP}.FN_ENTITY_MERGED`}
          components={{
            entityUrl: <NextLink className={classes.link} {...linkProps} />,
            bold: <span className={classes.bold} />,
            highlight: <span className={classes.highlightEntity} />,
          }}
          values={{
            entity: entityInfo?.name,
            newEntity,
          }}
        />
      </Alert>
    );
  }

  if (isEntityRebranded) {
    return (
      <Alert className={rootClassName} icon={false}>
        <CommonTrans
          ns="NS_APP_GLOBALS"
          i18nKey={`${TP}.FN_ENTITY_REBRANDED`}
          components={{
            entityUrl: <NextLink className={classes.link} {...linkProps} />,
            bold: <span className={classes.bold} />,
            highlight: <span className={classes.highlightEntity} />,
          }}
          values={{
            entity: entityInfo?.name,
            newEntity,
          }}
        />
      </Alert>
    );
  }

  if (isNonOperational) {
    return (
      <Alert className={rootClassName} severity="info">
        {t(`${TP}.FN_NON_OPERATIONAL_ENTITY`, { entity: entityInfo?.name })}
      </Alert>
    );
  }

  return null;
};

export default EntityStatus;
